import React from 'react';
import { Box, Button, Fab, Stack, SvgIcon, Typography } from '@mui/material';
import PlusIcon from '@heroicons/react/24/outline/PlusIcon';
import { useIsMobile } from '../hooks/use-is-mobile';
import { MobileActionSheet } from './mobile/mobile-action-sheet';
import { MOBILE_BOTTOM_NAV_HEIGHT } from '../dashboard/mobile/constants';

/**
 * actions: [{ label, icon, onClick, href, download, fileInput: { accept, onChange }, disabled, hidden }]
 */
export function PageHeader({ title, subtitle, actions = [], children }) {
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const fileInputRefs = React.useRef({});
  const visibleActions = actions.filter((action) => action && !action.hidden);

  const runAction = (action, index) => {
    if (action.fileInput) {
      fileInputRefs.current[index]?.click();
      return;
    }
    action.onClick?.();
  };

  const fileInputs = visibleActions.map((action, index) =>
    action.fileInput ? (
      <input
        key={`file-${index}`}
        ref={(element) => {
          fileInputRefs.current[index] = element;
        }}
        hidden
        type="file"
        accept={action.fileInput.accept}
        onChange={(event) => {
          action.fileInput.onChange(event);
          event.target.value = '';
        }}
      />
    ) : null
  );

  if (isMobile) {
    const singleAction = visibleActions.length === 1 ? visibleActions[0] : null;

    return (
      <>
        {fileInputs}
        {subtitle && (
          <Typography color="text.secondary" variant="body2">
            {subtitle}
          </Typography>
        )}
        {children && (
          <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
            {children}
          </Stack>
        )}
        {visibleActions.length > 0 && (
          <Fab
            color="primary"
            variant={singleAction ? 'extended' : 'circular'}
            aria-label={singleAction ? singleAction.label : `${title} actions`}
            disabled={singleAction?.disabled}
            {...(singleAction?.href && { component: 'a', href: singleAction.href, download: singleAction.download })}
            onClick={() => (singleAction ? runAction(singleAction, 0) : setSheetOpen(true))}
            sx={{
              position: 'fixed',
              right: 16,
              bottom: `calc(${MOBILE_BOTTOM_NAV_HEIGHT + 16}px + env(safe-area-inset-bottom))`,
              zIndex: (theme) => theme.zIndex.speedDial,
              color: 'primary.contrastText',
              boxShadow: 6,
            }}
          >
            <Box component="span" sx={{ display: 'flex', mr: singleAction ? 1 : 0 }}>
              {singleAction?.icon || (
                <SvgIcon fontSize="small">
                  <PlusIcon />
                </SvgIcon>
              )}
            </Box>
            {singleAction?.label}
          </Fab>
        )}
        {!singleAction && (
          <MobileActionSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            title={title}
            actions={visibleActions.map((action, index) => ({
              ...action,
              onClick: action.href ? undefined : () => runAction(action, index),
            }))}
          />
        )}
      </>
    );
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={4}>
      {fileInputs}
      <Box>
        <Typography component="h2" variant="h6">{title}</Typography>
        {subtitle && (
          <Typography color="text.secondary" variant="body2">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {children}
        {visibleActions.map((action, index) => (
          <Button
            key={action.label}
            disabled={action.disabled}
            startIcon={action.icon}
            variant="contained"
            {...(action.href && { component: 'a', href: action.href, download: action.download })}
            onClick={action.href ? undefined : () => runAction(action, index)}
            sx={{ color: 'neutral.100', m: 1 }}
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </Stack>
  );
}
